import * as React from "react"
import { loadMetadata, parseTimestamp } from "./audioHandlerUtils"
import AudioPlayerContext, {
  type AudioMetadata,
} from "../../context/AudioPlayerContext"
import { togglePlayPause } from "./buttonUtils"

interface AudioHandlerProps {
  setAudioMetadata: (data: AudioMetadata) => void
  audioMetadata: AudioMetadata | null
  setCurrentTime: (currentTime: number) => void
  currentTime: number
  onTrackChange: (track: Track | null) => void
  audioRef: React.RefObject<HTMLAudioElement>
  duration: number | null
  setDuration: (duration: number | null) => void
}

interface Track {
  title: string
  timestamp: string
}

const AudioHandler: React.FC<AudioHandlerProps> = ({
  setCurrentTime,
  onTrackChange,
  currentTime,
  audioMetadata,
  setAudioMetadata,
  duration,
  setDuration,
}) => {
  const audioPlayerContext = React.useContext(AudioPlayerContext)

  if (!audioPlayerContext) {
    return null
  }

  const { audioRef, isPlaying, setIsPlaying, trackInfo } = audioPlayerContext

  React.useEffect(() => {
    const fetchAndDecodeAudio = async () => {
      if (!trackInfo) {
        return
      }
      const audioData = await loadMetadata(trackInfo.metadataUrl)
      if (audioData) {
        setAudioMetadata(audioData)
      } else {
        //random values
        setAudioMetadata({
          data: Array.from({ length: 1000 }, () => Math.random()),
        })
      }
    }

    fetchAndDecodeAudio()
  }, [trackInfo, setAudioMetadata])

  React.useEffect(() => {
    if (!audioMetadata || !trackInfo) {
      return
    }
    const { trackList } = trackInfo
    const currentTrackIndex = trackList.findIndex((track, index) => {
      const trackStartTime = parseTimestamp(track.timestamp)
      let trackEndTime
      if (index === trackList.length - 1) {
        trackEndTime = duration
      } else {
        trackEndTime = parseTimestamp(trackList[index + 1].timestamp)
      }
      return currentTime >= trackStartTime && currentTime < trackEndTime
    })
    if (currentTrackIndex !== -1) {
      onTrackChange(trackList[currentTrackIndex])
    } else {
      onTrackChange(null)
    }
  }, [currentTime, duration, trackInfo, onTrackChange])

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const time = (e.target as HTMLAudioElement).currentTime
    setCurrentTime(time)
  }

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    if (e.currentTarget instanceof HTMLAudioElement) {
      // get duration from audio element
      const duration = e.currentTarget.duration
      setDuration(duration)
    }
  }

  React.useEffect(() => {
    if (trackInfo) {
      audioRef.current?.play()
      setIsPlaying(true)
    }
  }, [trackInfo])

  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        event.preventDefault()
        event.stopPropagation()
        togglePlayPause(isPlaying, audioRef.current, setIsPlaying)
      }
    }

    window.addEventListener("keydown", handleKeyPress)

    return () => {
      window.removeEventListener("keydown", handleKeyPress)
    }
  }, [setIsPlaying, isPlaying])

  if (!trackInfo) {
    return null
  }

  const handleEnded = () => {
    // Trigger loading a random post when audio ends
    audioPlayerContext?.switchToRandomPost();
  };

  return (
    <audio
      ref={audioRef}
      src={trackInfo.src}
      onTimeUpdate={handleTimeUpdate}
      onLoadedMetadata={handleLoadedMetadata}
      onEnded={handleEnded}
    />
  )
}

export default AudioHandler
